import { Loader2, MapPin } from 'lucide-react'
import { useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { toast } from 'sonner'

import { InstagramIcon, LogoIcon, MailIcon, PhoneIcon } from '@/assets/icons'
import Footer from '@/components/footer'
import Header from '@/components/header'
import { Button } from '@/components/ui/button'
import { useAppDispatch } from '@/hooks'
import { formatHoursList } from '@/lib/working-hours'
import platformService from '@/services/platform-service'
import storeService from '@/services/store-service'
import { setCurrentStore } from '@/slices/store'

export const StoreProfile = () => {
	const { storeRef = '' } = useParams()
	const navigate = useNavigate()
	const dispatch = useAppDispatch()

	const {
		data: store,
		isLoading,
		error,
	} = useQuery(['public-store', storeRef], () => storeService.getPublicStore(storeRef), {
		enabled: !!storeRef,
		refetchOnWindowFocus: false,
		onError: (err: any) => {
			toast.error(err?.response?.data?.message || 'Store nicht gefunden')
		},
		onSuccess: data => {
			dispatch(
				setCurrentStore({
					uuid: data.uuid,
					slug: data.slug,
					name: data.name,
					logo_url: data.logo_url,
					tagline: data.tagline,
					city: data.city,
					zip: data.zip,
					address: data.address,
					phone: data.phone,
					email: data.email,
					instagram: data.instagram,
					working_hours: data.working_hours,
					paypal_client_id: data.paypal_client_id,
					paypal_mode: data.paypal_mode,
				}),
			)
		},
	})

	const { data: packages } = useQuery('platform-packages-public', () => platformService.getPackages(), {
		refetchOnWindowFocus: false,
	})

	useEffect(() => {
		return () => {
			/* keep currentStore for booking navigation */
		}
	}, [])

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-[#F7F8F8]">
				<Loader2 className="animate-spin text-secondary" />
			</div>
		)
	}

	if (error || !store) {
		return (
			<div className="min-h-screen flex flex-col bg-[#F7F8F8]">
				<Header currentPage="startseite" />
				<div className="flex-1 flex flex-col items-center justify-center gap-3 px-5">
					<p className="text-secondary font-bold">Store nicht gefunden</p>
					<Button type="button" onClick={() => navigate('/')}>
						Zur Store-Suche
					</Button>
				</div>
				<Footer />
			</div>
		)
	}

	const hours = formatHoursList(store.working_hours)
	const addressLine = [store.address, [store.zip, store.city].filter(Boolean).join(' ')]
		.filter(Boolean)
		.join(', ')

	return (
		<div className="min-h-screen bg-[#F7F8F8] flex flex-col">
			<Header currentPage="startseite" store={store} />

			{store.cover_url ? (
				<div className="w-full h-48 lg:h-72 overflow-hidden bg-[#E8EEF6]">
					<img src={store.cover_url} alt="" className="w-full h-full object-cover" />
				</div>
			) : (
				<div className="w-full h-40 lg:h-56 bg-gradient-to-br from-[#E8EEF6] to-[#DDE8DF]" />
			)}

			<main className="mx-auto w-full max-w-5xl px-5 -mt-10 lg:-mt-14 relative z-10 flex-1 pb-12">
				<div className="rounded-2xl bg-white border border-[#EEEFF0] p-5 lg:p-8 shadow-sm">
					<div className="flex flex-col sm:flex-row gap-4 sm:items-end">
						<div className="size-24 lg:size-28 rounded-2xl border border-[#EEEFF0] bg-white overflow-hidden -mt-16 sm:-mt-20 shrink-0 flex items-center justify-center">
							{store.logo_url ? (
								<img src={store.logo_url} alt="" className="size-full object-cover" />
							) : (
								<LogoIcon className="size-12" />
							)}
						</div>
						<div className="flex-1 min-w-0">
							<h1 className="text-secondary text-2xl lg:text-3xl font-extrabold">{store.name}</h1>
							{store.tagline && (
								<p className="text-muted-foreground mt-1">{store.tagline}</p>
							)}
							{addressLine && (
								<p className="text-sm text-muted-foreground mt-2 inline-flex items-start gap-1.5">
									<MapPin className="size-4 shrink-0 mt-0.5" />
									{addressLine}
								</p>
							)}
						</div>
						<Button
							type="button"
							className="shrink-0 w-full sm:w-auto"
							onClick={() =>
								navigate(`/stores/${store.slug || store.uuid}/book`)
							}>
							Regal mieten
						</Button>
					</div>

					{store.description && (
						<p className="mt-6 text-secondary/90 whitespace-pre-line leading-relaxed">
							{store.description}
						</p>
					)}

					<div className="mt-8 grid gap-8 lg:grid-cols-2">
						<div>
							<h2 className="text-secondary font-extrabold mb-3">Öffnungszeiten</h2>
							<ul className="space-y-2">
								{hours.map(row => (
									<li
										key={row.day}
										className="flex justify-between gap-4 text-sm border-b border-[#EEEFF0] pb-2">
										<span className="font-semibold text-secondary w-8">{row.day}</span>
										<span className="text-muted-foreground text-right">{row.text}</span>
									</li>
								))}
							</ul>
						</div>
						<div>
							<h2 className="text-secondary font-extrabold mb-3">Kontakt</h2>
							<ul className="space-y-3 text-sm">
								{store.phone && (
									<li className="flex items-center gap-2 text-muted-foreground">
										<PhoneIcon className="size-5 shrink-0" />
										{store.phone}
									</li>
								)}
								{store.email && (
									<li className="flex items-center gap-2 text-muted-foreground">
										<MailIcon className="size-5 shrink-0" />
										<a href={`mailto:${store.email}`} className="hover:text-secondary">
											{store.email}
										</a>
									</li>
								)}
								{store.instagram && (
									<li className="flex items-center gap-2 text-muted-foreground">
										<InstagramIcon className="size-5 shrink-0" />
										<a
											href={`https://instagram.com/${store.instagram.replace(/^@/, '')}`}
											target="_blank"
											rel="noreferrer"
											className="hover:text-secondary">
											@{store.instagram.replace(/^@/, '')}
										</a>
									</li>
								)}
							</ul>
						</div>
					</div>
				</div>

				{packages?.packages?.length ? (
					<section className="mt-8">
						<h2 className="text-secondary text-xl font-extrabold mb-2">Pakete</h2>
						<p className="text-muted-foreground text-sm mb-4">
							Gleiche Preise an allen Stores — Plattform-Tarife
						</p>
						<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
							{packages.packages.map(pkg => (
								<div
									key={pkg.id}
									className="rounded-2xl bg-white border border-[#EEEFF0] p-4">
									<p className="font-extrabold text-secondary">{pkg.title}</p>
									<p className="text-2xl font-extrabold text-secondary mt-1">
										{pkg.price === 0 ? '0 €' : `${pkg.price} €`}
									</p>
									<p className="text-xs text-muted-foreground mt-1">
										{pkg.provision}% Provision · {pkg.duration} Tage
									</p>
								</div>
							))}
						</div>
					</section>
				) : null}

				<p className="mt-8 text-center text-sm text-muted-foreground">
					<Link to="/" className="text-secondary font-semibold hover:underline">
						Alle Stores
					</Link>
				</p>
			</main>

			<Footer store={store} />
		</div>
	)
}
